import { DtoExt } from '@/classes/scenario/DtoExt';
import { EcoBreakdownExt } from '@/classes/scenario/eco/EcoBreakdownExt';
import { EcoFutureExt } from '@/classes/scenario/eco/EcoFutureExt';
import { EcoOMExt } from '@/classes/scenario/eco/EcoOMExt';
import {
  DepreciationTypeRef,
  GridPricingTypeRef,
  ProjectFinancingStructureTypeRef
} from '@/dto/scenario/eco/EcoReference';
import { ScenarioEco } from '@/dto/scenario/eco/ScenarioEco';
import { GridComplexPricingExt } from '@/classes/scenario/grid/GridComplexPricingExt';
import { GridPricingFileExt } from './GridPricingFileExt';
import { EcoRevenuesExt } from '@/classes/scenario/eco/EcoRevenuesExt';
import { FeedInExt } from '@/classes/scenario/eco/FeedInExt';

export class ScenarioEcoExt extends DtoExt {
  public static fromDto(dto: ScenarioEco): ScenarioEcoExt {
    return new ScenarioEcoExt({
      hasBreakdownCapex: dto.hasBreakdownCapex,
      epcProposalFixed: dto.epcProposalFixed,
      epcProposalVariable: dto.epcProposalVariable,
      breakdown: dto.breakdown && EcoBreakdownExt.fromDto(dto.breakdown),
      future: dto.future && EcoFutureExt.fromDto(dto.future),
      om: dto.om && EcoOMExt && EcoOMExt.fromDto(dto.om),
      gridFixFee: dto.gridFixFee,
      gridPrice: dto.gridPrice,
      gridMyFilePricing: dto.gridMyFilePricing && GridPricingFileExt.fromDto(dto.gridMyFilePricing),
      gridPricingTypeRef: dto.gridPricingTypeRef,
      gridFixFeeEscalationRate: dto.gridFixFeeEscalationRate,
      gridPriceEscalationRate: dto.gridPriceEscalationRate,
      gridComplexPricing: dto.gridComplexPricing && GridComplexPricingExt.fromDto(dto.gridComplexPricing),
      gridFixFeeTax: dto.gridFixFeeTax,
      gridPriceTax: dto.gridPriceTax,
      fuelPrice: dto.fuelPrice,
      fuelEscalationRate: dto.fuelEscalationRate,
      fuelPriceType1: dto.fuelPriceType1,
      fuelEscalationRateType1: dto.fuelEscalationRateType1,
      fuelPriceType2: dto.fuelPriceType2,
      fuelEscalationRateType2: dto.fuelEscalationRateType2,
      feedIn: new FeedInExt({ ...dto, feedInFlexibleTariff: GridPricingFileExt.fromDto(dto.feedInFlexibleTariff ?? {}) }),
      useAffiliateDebt: dto.useAffiliateDebt,
      useFarmdown: dto.useFarmdown,
      affiliateGearing: dto.affiliateGearing,
      minimumDscr: dto.minimumDscr,
      tenor: dto.tenor,
      shortTermInterestRate: dto.shortTermInterestRate,
      gensetTaxes: dto.gensetTaxes,
      shelterTaxes: dto.shelterTaxes,
      pvModuleTaxes: dto.pvModuleTaxes,
      pvRackingTaxes: dto.pvRackingTaxes,
      pvInverterTaxes: dto.pvInverterTaxes,
      batteryTaxes: dto.batteryTaxes,
      installationTaxes: dto.installationTaxes,
      additionalCashFlow: dto.additionalCashFlow,
      fixedYearlyCashFlowOtherCost: dto.fixedYearlyCashFlowOtherCost,
      fixedYearlyCashFlowOtherCostYearsAffected: dto.fixedYearlyCashFlowOtherCostYearsAffected,
      totalAmountOverConstruction: dto.totalAmountOverConstruction,
      revenues: new EcoRevenuesExt(dto),
      corporateTaxRate: dto.corporateTaxRate,
      discountRate: dto.discountRate,
      currency: dto.currency,
      exchangeRate: dto.exchangeRate,
      inflationRateLocalCurrency: dto.inflationRateLocalCurrency,
      inflationRateUSD: dto.inflationRateUSD,
      projectFinancingStructureTypeRef: dto.projectFinancingStructureTypeRef,
      lossCarryForwardLimit: dto.lossCarryForwardLimit,
      withholdingTaxOnDividends: dto.withholdingTaxOnDividends,
      taxHoliday: dto.taxHoliday,
      depreciationTypeRef: dto.depreciationTypeRef,
      depreciationTime: dto.depreciationTime,
      depreciationPercentage: dto.depreciationPercentage,
      hasTaxCredit: dto.hasTaxCredit,
      taxCreditRate: dto.taxCreditRate,
      taxCreditYear: dto.taxCreditYear
    });
  }

  // CAPEX
  public hasBreakdownCapex: boolean | null = null;
  public epcProposalFixed: number | null = null;
  public epcProposalVariable: number | null = null;
  public breakdown: EcoBreakdownExt | null = null;
  public future: EcoFutureExt | null = null;
  public om: EcoOMExt | null = null;

  // GRID
  public gridFixFee: number | null = null;
  public gridPrice: number | null = null;
  public gridMyFilePricing: GridPricingFileExt | null = new GridPricingFileExt();
  public gridFixFeeEscalationRate: number | null = null;
  public gridPriceEscalationRate: number | null = null;
  public gridPricingTypeRef: GridPricingTypeRef | null = null;
  public gridComplexPricing?: GridComplexPricingExt | null = null;

  public gridFixFeeTax: number | null = 0;
  public gridPriceTax: number | null = 0;

  public fuelPrice: number | null = null;
  public fuelEscalationRate: number | null = null;

  public fuelPriceType1: number | null = null;
  public fuelEscalationRateType1: number | null = null;

  public fuelPriceType2: number | null = null;
  public fuelEscalationRateType2: number | null = null;

  public feedIn = new FeedInExt();

  // AFFILIATE DEBT
  public useAffiliateDebt: boolean | null = null;
  public useFarmdown: boolean | null = null;
  public affiliateGearing: number | null = null;
  public minimumDscr: number | null = null;
  public tenor: number | null = null;
  public shortTermInterestRate: number | null = null;

  // IMPORT TAXES
  public gensetTaxes: number | null = null;
  public shelterTaxes: number | null = null;
  public pvModuleTaxes: number | null = null;
  public pvRackingTaxes: number | null = null;
  public pvInverterTaxes: number | null = null;
  public batteryTaxes: number | null = null;
  public installationTaxes: number | null = null;

  // FIXED YEARLY CASH FLOW
  public additionalCashFlow: boolean | null = null;
  public fixedYearlyCashFlowOtherCost: number | null = null;
  public fixedYearlyCashFlowOtherCostYearsAffected: number | null = null;
  public totalAmountOverConstruction: number | null = null;

  // REVENUES
  public revenues: EcoRevenuesExt = new EcoRevenuesExt();

  // OTHERS
  public corporateTaxRate: number | null = null;
  public discountRate: number | null = null;
  public currency: string | null = null; // MAYBE ENUM ?
  public exchangeRate: number | null = null;
  public inflationRateLocalCurrency: number | null = null;
  public inflationRateUSD: number | null = null;

  // PROJECT FINANCING STRUCTURE
  public projectFinancingStructureTypeRef: ProjectFinancingStructureTypeRef | null = null;
  public lossCarryForwardLimit: number | null = null;
  public withholdingTaxOnDividends: number | null = null;
  public taxHoliday: number | null = null;

  // DEPRECIATION
  public depreciationTypeRef: DepreciationTypeRef | null = null;
  public depreciationTime: number | null = null;
  public depreciationPercentage: number | null = null;

  // TAX CREDIT
  public hasTaxCredit: boolean | null = null;
  public taxCreditRate: number | null = null;
  public taxCreditYear: number | null = null;

  constructor(options?: Partial<ScenarioEcoExt>) {
    super();
    if (options) {
      DtoExt.assignToThis(this, options);
    }
  }

  public toDto(): ScenarioEco {
    return new ScenarioEco({
      hasBreakdownCapex: this.hasBreakdownCapex,
      epcProposalFixed: this.epcProposalFixed,
      epcProposalVariable: this.epcProposalVariable,
      breakdown: this.breakdown && this.breakdown.toDto(),
      future: this.future && this.future.toDto(),
      om: this.om && this.om.toDto(),
      gridPricingTypeRef: this.gridPricingTypeRef,
      gridFixFee: this.gridFixFee,
      gridPrice: this.gridPrice,
      gridMyFilePricing: this.gridMyFilePricing && this.gridMyFilePricing.toDto(),
      gridFixFeeEscalationRate: this.gridFixFeeEscalationRate,
      gridPriceEscalationRate: this.gridPriceEscalationRate,
      gridComplexPricing: this.gridComplexPricing && this.gridComplexPricing.toDto(),
      gridFixFeeTax: this.gridFixFeeTax,
      gridPriceTax: this.gridPriceTax,
      fuelPrice: this.fuelPrice,
      fuelEscalationRate: this.fuelEscalationRate,
      fuelPriceType1: this.fuelPriceType1,
      fuelEscalationRateType1: this.fuelEscalationRateType1,
      fuelPriceType2: this.fuelPriceType2,
      fuelEscalationRateType2: this.fuelEscalationRateType2,
      ...this.feedIn,
      useAffiliateDebt: this.useAffiliateDebt,
      useFarmdown: this.useFarmdown,
      affiliateGearing: this.affiliateGearing,
      minimumDscr: this.minimumDscr,
      tenor: this.tenor,
      shortTermInterestRate: this.shortTermInterestRate,
      gensetTaxes: this.gensetTaxes,
      shelterTaxes: this.shelterTaxes,
      pvModuleTaxes: this.pvModuleTaxes,
      pvRackingTaxes: this.pvRackingTaxes,
      pvInverterTaxes: this.pvInverterTaxes,
      batteryTaxes: this.batteryTaxes,
      installationTaxes: this.installationTaxes,
      additionalCashFlow: this.additionalCashFlow,
      fixedYearlyCashFlowOtherCost: this.fixedYearlyCashFlowOtherCost,
      fixedYearlyCashFlowOtherCostYearsAffected: this.fixedYearlyCashFlowOtherCostYearsAffected,
      totalAmountOverConstruction: this.totalAmountOverConstruction,
      ...this.revenues,
      corporateTaxRate: this.corporateTaxRate,
      discountRate: this.discountRate,
      currency: this.currency,
      exchangeRate: this.exchangeRate,
      inflationRateLocalCurrency: this.inflationRateLocalCurrency,
      inflationRateUSD: this.inflationRateUSD,
      projectFinancingStructureTypeRef: this.projectFinancingStructureTypeRef,
      lossCarryForwardLimit: this.lossCarryForwardLimit,
      withholdingTaxOnDividends: this.withholdingTaxOnDividends,
      taxHoliday: this.taxHoliday,
      depreciationTypeRef: this.depreciationTypeRef,
      depreciationTime: this.depreciationTime,
      depreciationPercentage: this.depreciationPercentage,
      hasTaxCredit: this.hasTaxCredit,
      taxCreditRate: this.taxCreditRate,
      taxCreditYear: this.taxCreditYear
    });
  }
}
